<template>
  <!-- Overview for a brand with no ad data, and no Meta ad library for us to scrape -->
  <div class="w-full px-8 pt-8 pb-16">
    <div class="w-full mx-auto" style="max-width: 1000px;">
      <!-- No Ad Library prompt -->
      <div class="flex flex-col items-start gap-5 fade-in">
        <div class="flex flex-col max-w-full" style="width: 578px;">
          <div class="flex items-center gap-2 min-w-0">
            <BrandAvatar :avatar-src="brand.avatar_url" :brand-name="brand.name" :width="20" :height="20"
            class="flex-shrink-0" style="border-radius: 4.5px" />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ brand.name }} doesn't have a Meta Ad Library page
            </BaseText>
          </div>
          <BaseText type="body" size="sm" class="text-text-normal mt-2.5">
            This means there are no ads for us to collect.
            <br/>
            To see ads saved for this brand by other users, visit the Community Ads tab.
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal mt-4">
            If there is an ad library page for this brand, you can add it to Spyder by pasting the link.
          </BaseText>
        </div>
        <div class="flex items-center gap-2">
          <button class="secondary-button flex items-center gap-0.5 px-1.5 py-1 rounded-md bg-white" @click="$emit('view-community-ads')">
            <CommunityFeedIcon class="text-icon-normal flex-shrink-0" />
            <BaseText type="label" size="sm" class="px-1 text-text-muted">
              View Community Ads
            </BaseText>
          </button>
          <button class="secondary-button flex items-center gap-0.5 px-1.5 py-1 rounded-md bg-white" @click="addByAdLibraryLink">
            <LinkRoundedIcon class="text-icon-normal flex-shrink-0" />
            <BaseText type="label" size="sm" class="px-1 text-text-muted">
              Add by Ad Library Link
            </BaseText>
          </button>
        </div>
      </div>
      <!-- Brands to look at instead: left out when there are none to show -->
      <div v-if="loadingBrands || suggestedBrands.length" class="flex flex-col gap-3 mt-12 fade-in">
        <BaseText type="body" size="sm" class="text-text-normal">
          Similar brands that may have what you're looking for
        </BaseText>
        <div class="grid grid-cols-3 gap-3">
          <!-- Loading -->
          <template v-if="loadingBrands">
            <div v-for="n in skeletonCount" :key="`brand-skeleton-${n}`"
            class="flex flex-col p-4 min-w-0 rounded-xl border border-border-normal bg-white" style="gap: 22px;">
              <div class="flex items-center gap-3">
                <div class="skeleton w-11 h-11 rounded-md flex-shrink-0" />
                <div class="flex-grow flex flex-col gap-1">
                  <div class="flex items-center h-5">
                    <div class="skeleton w-1/2 h-3.5 rounded" />
                  </div>
                  <div class="flex items-center h-5">
                    <div class="skeleton w-1/3 h-3.5 rounded" />
                  </div>
                </div>
              </div>
              <div class="flex flex-col">
                <div v-for="(lineWidth, lineIndex) in ['100%', '100%', '66%']" :key="`description-skeleton-${n}-${lineIndex}`"
                class="flex items-center h-5">
                  <div class="skeleton h-3.5 rounded" :style="{ width: lineWidth }" />
                </div>
              </div>
              <div class="skeleton w-28 rounded-md" style="height: 22px;" />
            </div>
          </template>
          <!-- Brands -->
          <template v-else>
            <router-link v-for="suggestedBrand in suggestedBrands" :key="`suggested-brand-${suggestedBrand.id}`"
            :to="{ name: 'SpyderView', params: { id: suggestedBrand.id } }"
            class="brand-card flex flex-col p-4 min-w-0 rounded-xl border border-border-normal bg-white transition-colors
            hover:border-border-hover" style="gap: 22px;">
              <div class="flex items-center gap-3 min-w-0">
                <BrandAvatar :avatar-src="suggestedBrand.avatar" :brand-name="suggestedBrand.name" :width="44" :height="44"
                class="rounded-md flex-shrink-0" />
                <div class="flex flex-col gap-1 min-w-0">
                  <BaseText type="label" size="sm" class="text-text-muted truncate">
                    {{ suggestedBrand.name }}
                  </BaseText>
                  <BaseText v-if="suggestedBrand.domain" type="body" size="sm" class="text-text-normal truncate">
                    {{ suggestedBrand.domain }}
                  </BaseText>
                </div>
              </div>
              <BaseText v-if="suggestedBrand.description" type="body" size="sm" class="brand-description text-text-normal">
                {{ suggestedBrand.description }}
              </BaseText>
              <!-- Pinned to the bottom, so badges line up across a row when a brand has no description -->
              <div class="flex mt-auto">
                <div class="flex items-center p-0.5 rounded-md border border-border-normal bg-white whitespace-nowrap">
                  <BaseText type="label" size="xs" class="px-1 text-text-muted">
                    Tracked by
                  </BaseText>
                  <SpyderIcon :width="16" :height="16" class="text-icon-muted flex-shrink-0" />
                  <BaseText type="label" size="xs" class="px-1 text-text-muted">
                    {{ suggestedBrand.trackedByCount }}
                  </BaseText>
                </div>
              </div>
            </router-link>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSuggestedBrands } from '@/api/services/SuggestedBrandsService'
import { getBrandPrimaryDomain } from '@/utils/spyder/brandUtils'
import formatLabelString from '@/utils/lens/formatGraphLabelString'

// Components
import BrandAvatar from '../../../brands/BrandAvatar.vue'

// Icons
import SpyderIcon from '../../../globals/Icons/SidebarIcons/SpyderIcon.vue'
import CommunityFeedIcon from '../../../globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'
import LinkRoundedIcon from '../../../globals/Icons/LinkRoundedIcon.vue'

// Two rows of the 3 column grid
const SUGGESTED_BRANDS_COUNT = 6

// Flattens a Spyder brand (from the /brands search, or a /similar hit's `brand`) into what a brand card renders
const normalizeSuggestedBrand = (brand) => ({
  id: brand.id,
  name: brand.name,
  avatar: brand.avatar_url ?? null,
  domain: getBrandPrimaryDomain(brand),
  description: brand.enrichment?.description ?? brand.description ?? null,
  trackedByCount: formatLabelString(brand.subscriber_count ?? 0, 'number')
})

export default {
  name: 'UntrackableBrandOverview',
  components: {
    BrandAvatar,
    SpyderIcon,
    CommunityFeedIcon,
    LinkRoundedIcon
  },
  props: {
    // Loaded brand from the /brands endpoint
    brand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      suggestedBrands: [],
      loadingBrands: true,
      skeletonCount: SUGGESTED_BRANDS_COUNT
    }
  },
  mounted () {
    this.fetchSuggestedBrands()
  },
  methods: {
    async fetchSuggestedBrands () {
      const brands = await getSuggestedBrands(this.brand, SUGGESTED_BRANDS_COUNT)
      this.suggestedBrands = brands.map(normalizeSuggestedBrand)
      this.loadingBrands = false
    },
    addByAdLibraryLink () {
      this.$router.push({ name: 'SpyderBrandsView', query: { add: 'manual' } })
    }
  }
}
</script>

<style scoped>
.secondary-button {
  box-shadow: 0px 1px 2px 0px rgba(9, 25, 72, 0.13), 0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  transition: background-color 100ms ease-in-out;
}
.secondary-button:hover {
  background-color: #F6F8FA; /* bg-neutral-25 */
}

.brand-card {
  animation: fadeIn 300ms ease-in-out;
}
.brand-description {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
